import { customElement } from 'lit/decorators.js';
import { html, css } from 'lit';
import { property } from 'lit/decorators.js';
import { RDPropsetContainer } from '../Container/PropContainer';

interface BoxModelValues {
  marginTop: string;
  marginRight: string;
  marginBottom: string;
  marginLeft: string;
  paddingTop: string;
  paddingRight: string;
  paddingBottom: string;
  paddingLeft: string;
}
@customElement('rd-box-model-prop')

///@ts-ignore
export class RDBoxModelProp extends RDPropsetContainer {
  static styles = [
    RDPropsetContainer.styles,
    css`
      /* Variable Definitions for Theme */
      :host {
        --margin-bg: #f9cc9d; /* Orange-ish Chrome DevTools style */
        --margin-text: #a85d0d;
        --padding-bg: #c3dfb8; /* Green-ish Chrome DevTools style */
        --padding-text: #507e3a;
        --content-bg: #9abce6; /* Blue-ish */
        --content-text: #2a5585;
        --input-text-color: #222;
        --input-placeholder: #666;
        --box-font-size: 11px;
      }

      /* Dark Mode Adjustments (optional, based on host context) */
      :host-context(.dark) {
        --margin-bg: #5c3b18;
        --padding-bg: #2d4526;
        --content-bg: #263b52;
        --input-text-color: #eee;
      }

      .box-model-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 10px 0;
        font-family: monospace; /* Monospace looks best for layout tools */
        user-select: none;
      }

      /* The Concentric Boxes */
      .box-layer {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 1px dashed rgba(0, 0, 0, 0.1);
        transition: all 0.2s ease;
      }

      .margin-box {
        width: 100%;
        max-width: 280px;
        height: 160px;
        background-color: var(--margin-bg);
        color: var(--margin-text);
        border-radius: 4px;
        position: relative;
      }

      .padding-box {
        width: calc(100% - 70px);
        height: calc(100% - 60px);
        background-color: var(--padding-bg);
        color: var(--padding-text);
        border-radius: 3px;
        position: relative;
      }

      .content-box {
        width: calc(100% - 70px);
        height: calc(100% - 60px);
        background-color: var(--content-bg);
        color: var(--content-text);
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 2px;
        font-size: 10px;
        text-align: center;
      }

      /* Labels (MARGIN, PADDING) */
      .box-label {
        position: absolute;
        top: 2px;
        left: 4px;
        font-size: 9px;
        opacity: 0.8;
        pointer-events: none;
        font-weight: bold;
        letter-spacing: 0.5px;
      }

      /* Input Styling */
      input.side-input {
        position: absolute;
        width: 34px;
        height: 20px;
        background: transparent;
        border: none;
        text-align: center;
        font-size: var(--box-font-size);
        color: var(--input-text-color);
        cursor: text;
        padding: 0;
        margin: 0;
        font-family: inherit;
        outline: none;
        border-radius: 2px;
      }

      input.side-input:hover {
        background: rgba(255, 255, 255, 0.2);
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
      }

      input.side-input:focus {
        background: #fff;
        color: #000;
        box-shadow: 0 0 0 2px #3b82f6;
        z-index: 10;
      }

      /* Positioning the inputs */
      .pos-top {
        top: 4px;
        left: 50%;
        transform: translateX(-50%);
      }
      .pos-bottom {
        bottom: 4px;
        left: 50%;
        transform: translateX(-50%);
      }
      .pos-left {
        left: 2px;
        top: 50%;
        transform: translateY(-50%);
      }
      .pos-right {
        right: 2px;
        top: 50%;
        transform: translateY(-50%);
      }

      /* Specific tweaks for inner boxes to align nicely */
      .margin-box > .pos-left,
      .margin-box > .pos-right {
        width: 30px;
      }
      .padding-box > .pos-left,
      .padding-box > .pos-right {
        width: 30px;
      }

      /* Unit Indicator (optional, visible on hover) */
      .unit-indicator {
        position: absolute;
        font-size: 8px;
        opacity: 0.5;
        pointer-events: none;
      }
    `,
  ];

  @property({ type: Object }) values: BoxModelValues = {
    marginTop: '',
    marginRight: '',
    marginBottom: '',
    marginLeft: '',
    paddingTop: '',
    paddingRight: '',
    paddingBottom: '',
    paddingLeft: '',
  };

  constructor() {
    super();
    this.label = 'Layout';
    // If you need to initialize specific groups from parent:
    // this.iconGroups = [{ name: 'Lock', items: [{ icon: icons.unlock, name: 'link', selected: false }] }];
  }

  // Handle input changes
  _handleInputChange(e: Event, property: keyof BoxModelValues) {
    const input = e.target as HTMLInputElement;
    let val = input.value.trim();

    // Basic UX: If user types "10", assume "px" unless they typed a unit
    // If empty or "-", keep as is.
    if (val !== '' && val !== '-' && val !== 'auto' && !isNaN(Number(val))) {
      val += 'px';
    }

    // Update local state
    this.values = { ...this.values, [property]: val };
    this.modified = true;

    // Dispatch event to parent
    this.dispatchEvent(
      new CustomEvent('property-change', {
        detail: {
          property: property,
          value: val,
          allValues: this.values,
        },
        bubbles: true,
        composed: true,
      })
    );

    this.requestUpdate();
  }

  // Handle focusing (select all text)
  _handleFocus(e: Event) {
    (e.target as HTMLInputElement).select();
  }

  // Helper to render an input
  renderInput(prop: keyof BoxModelValues, positionClass: string, placeholder = '-') {
    return html`
      <input
        type="text"
        class="side-input ${positionClass}"
        .value="${(this.values as any)[prop] || ''}"
        placeholder="${placeholder}"
        @change="${(e: Event) => this._handleInputChange(e, prop)}"
        @keydown="${(e: KeyboardEvent) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}"
        @focus="${this._handleFocus}"
      />
    `;
  }

  renderBody() {
    return html`
      <div class="box-model-wrapper">
        <!-- MARGIN BOX -->
        <div class="box-layer margin-box">
          <span class="box-label">MARGIN</span>

          ${this.renderInput('marginTop', 'pos-top')} ${this.renderInput('marginRight', 'pos-right')} ${this.renderInput('marginBottom', 'pos-bottom')} ${this.renderInput('marginLeft', 'pos-left')}

          <!-- PADDING BOX -->
          <div class="box-layer padding-box">
            <span class="box-label">PADDING</span>

            ${this.renderInput('paddingTop', 'pos-top')} ${this.renderInput('paddingRight', 'pos-right')} ${this.renderInput('paddingBottom', 'pos-bottom')} ${this.renderInput('paddingLeft', 'pos-left')}

            <!-- CONTENT BOX -->
            <div class="box-layer content-box">
              <span>CONTENT</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
